import { useState, useCallback, useEffect } from 'react';
import {
  ShieldAlert, Search, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Filter, ShieldCheck, AlertTriangle,
  Calendar,
} from 'lucide-react';

import { securityAuditApi } from '@/api/securityAudit.api';
import type { SecurityAuditLogDto, SecurityAuditFilterDto } from '@/api/securityAudit.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

// ── Helpers ────────────────────────────────────────────────────────────────────

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

// Colour-coded event type badge
const EVENT_COLORS: Record<string, string> = {
  LOGIN:              'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  LOGOUT:             'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  LOGIN_FAILED:       'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  ACCOUNT_LOCKED:     'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  ACCOUNT_UNLOCKED:   'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  PASSWORD_CHANGED:   'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  PASSWORD_RESET:     'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  USER_CREATED:       'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  USER_UPDATED:       'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  USER_DISABLED:      'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  USER_ENABLED:       'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  ROLE_ASSIGNED:      'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  SESSION_TERMINATED: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  POLICY_UPDATED:     'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
};

const eventBadgeClass = (eventType: string) =>
  EVENT_COLORS[eventType] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';

const formatEventType = (et: string) =>
  et.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ── Filter bar ─────────────────────────────────────────────────────────────────

const KNOWN_EVENT_TYPES = [
  'LOGIN', 'LOGOUT', 'LOGIN_FAILED',
  'ACCOUNT_LOCKED', 'ACCOUNT_UNLOCKED',
  'PASSWORD_CHANGED', 'PASSWORD_RESET',
  'USER_CREATED', 'USER_UPDATED', 'USER_DISABLED', 'USER_ENABLED',
  'ROLE_ASSIGNED', 'SESSION_TERMINATED', 'POLICY_UPDATED',
];

interface FiltersProps {
  filter:    SecurityAuditFilterDto;
  onChange:  (patch: Partial<SecurityAuditFilterDto>) => void;
  onApply:   () => void;
  onReset:   () => void;
  expanded:  boolean;
  onToggle:  () => void;
}

const FilterBar = ({ filter, onChange, onApply, onReset, expanded, onToggle }: FiltersProps) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50"
    >
      <span className="flex items-center gap-2">
        <Filter size={14} />
        Advanced Filters
      </span>
      <ChevronLeft size={14} className={cn('transition-transform', expanded ? '-rotate-90' : 'rotate-90')} />
    </button>

    {expanded && (
      <div className="border-t border-gray-100 dark:border-gray-800 px-4 pb-4 pt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Event Type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Event Type</label>
          <select
            value={filter.eventType ?? ''}
            onChange={e => onChange({ eventType: e.target.value || undefined })}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All events</option>
            {KNOWN_EVENT_TYPES.map(et => (
              <option key={et} value={et}>{formatEventType(et)}</option>
            ))}
          </select>
        </div>

        {/* Outcome */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Outcome</label>
          <select
            value={filter.success === undefined ? '' : String(filter.success)}
            onChange={e => onChange({ success: e.target.value === '' ? undefined : e.target.value === 'true' })}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All outcomes</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
        </div>

        {/* Date From */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">From</label>
          <input
            type="date"
            value={filter.dateFrom ?? ''}
            onChange={e => onChange({ dateFrom: e.target.value || undefined })}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Date To */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">To</label>
          <input
            type="date"
            value={filter.dateTo ?? ''}
            onChange={e => onChange({ dateTo: e.target.value || undefined })}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Actor search */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Search</label>
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Actor or target username…"
              value={filter.search ?? ''}
              onChange={e => onChange({ search: e.target.value || undefined })}
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="col-span-full flex items-center gap-2 justify-end pt-1">
          <button
            type="button"
            onClick={onReset}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onApply}
            className="px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
          >
            Apply Filters
          </button>
        </div>
      </div>
    )}
  </div>
);

// ── Audit log row ──────────────────────────────────────────────────────────────

const AuditRow = ({ entry }: { entry: SecurityAuditLogDto }) => (
  <div className="grid grid-cols-[auto_1fr_auto] gap-4 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
    {/* Outcome icon */}
    <div className="mt-1 flex-shrink-0">
      {entry.success
        ? <CheckCircle2 size={15} className="text-green-500" />
        : <XCircle      size={15} className="text-red-500" />
      }
    </div>

    {/* Main info */}
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn(
          'text-xs font-semibold px-2 py-0.5 rounded-full',
          eventBadgeClass(entry.eventType),
        )}>
          {formatEventType(entry.eventType)}
        </span>
        {entry.actorUsername && (
          <span className="text-xs text-gray-600 dark:text-gray-400">
            by <span className="font-medium">{entry.actorUsername}</span>
          </span>
        )}
        {entry.targetUsername && entry.targetUsername !== entry.actorUsername && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            → <span className="font-medium">{entry.targetUsername}</span>
          </span>
        )}
      </div>

      {entry.description && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate max-w-xl">{entry.description}</p>
      )}

      <div className="flex items-center gap-3 mt-1 flex-wrap">
        {entry.ipAddress && (
          <span className="text-xs text-gray-400 dark:text-gray-500">{entry.ipAddress}</span>
        )}
        {(entry.oldValue || entry.newValue) && (
          <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
            {entry.oldValue && <span className="line-through text-red-400">{entry.oldValue.slice(0, 40)}</span>}
            {entry.oldValue && entry.newValue && ' → '}
            {entry.newValue && <span className="text-green-600 dark:text-green-400">{entry.newValue.slice(0, 40)}</span>}
          </span>
        )}
      </div>
    </div>

    {/* Timestamp */}
    <div className="flex-shrink-0 text-right">
      <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
        <Calendar size={11} />
        <span className="whitespace-nowrap">{formatDateTime(entry.createdDate)}</span>
      </div>
    </div>
  </div>
);

// ── Page ───────────────────────────────────────────────────────────────────────

const DEFAULT_FILTER: SecurityAuditFilterDto = { pageNumber: 1, pageSize: 50 };

export const SecurityAuditPage = () => {
  const { hasPermission } = usePermission();
  const canView = hasPermission(Permissions.SECURITY_AUDIT_VIEW);

  const [entries,       setEntries]       = useState<SecurityAuditLogDto[]>([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState<SecurityAuditFilterDto>(DEFAULT_FILTER);
  const [pendingFilter, setPendingFilter] = useState<SecurityAuditFilterDto>(DEFAULT_FILTER);
  const [filtersOpen,   setFiltersOpen]   = useState(false);
  const [toast,         setToast]         = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async (f: SecurityAuditFilterDto) => {
    setLoading(true);
    try {
      const res   = await securityAuditApi.getList(f);
      const paged = res.data.data;
      setEntries(paged?.items ?? []);
      setTotal(paged?.totalCount ?? 0);
    } catch {
      showToast('error', 'Failed to load security audit log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const applyFilters = () => {
    const next = { ...pendingFilter, pageNumber: 1 };
    setFilter(next);
  };

  const resetFilters = () => {
    setPendingFilter(DEFAULT_FILTER);
    setFilter(DEFAULT_FILTER);
  };

  const setPage = (p: number) => setFilter(prev => ({ ...prev, pageNumber: p }));

  const currentPage = filter.pageNumber ?? 1;
  const pageSize    = filter.pageSize ?? 50;
  const totalPages  = Math.max(1, Math.ceil(total / pageSize));

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <ShieldCheck size={20} className="mr-2" /> You do not have permission to view the security audit log.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <ShieldAlert size={20} className="text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Security Audit Log</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Immutable record of all security-relevant events.
            </p>
          </div>
        </div>

        <button
          onClick={() => load(filter)}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
        >
          <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={cn(
          'flex items-center gap-2 rounded-lg px-4 py-3 text-sm',
          toast.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800',
        )}>
          {toast.type === 'success' ? <ShieldCheck size={16} /> : <AlertTriangle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Filter bar */}
      <FilterBar
        filter={pendingFilter}
        onChange={patch => setPendingFilter(prev => ({ ...prev, ...patch }))}
        onApply={applyFilters}
        onReset={resetFilters}
        expanded={filtersOpen}
        onToggle={() => setFiltersOpen(o => !o)}
      />

      {/* Summary stats */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <span className="font-semibold text-gray-900 dark:text-gray-100">{total.toLocaleString()}</span>
        entries
        {(filter.eventType || filter.success !== undefined || filter.dateFrom || filter.search) && (
          <span className="text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-full">filtered</span>
        )}
      </div>

      {/* Log table */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
        {/* Table header */}
        <div className="grid grid-cols-[auto_1fr_auto] gap-4 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-4">OK</span>
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Event</span>
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Time</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Spinner />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400 dark:text-gray-500 gap-2">
            <ShieldAlert size={32} className="text-gray-300" />
            <p className="text-sm">No audit entries match your filters.</p>
          </div>
        ) : (
          <div>
            {entries.map(e => <AuditRow key={e.id} entry={e} />)}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Page {currentPage} of {totalPages} · {total.toLocaleString()} entries
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 disabled:opacity-40"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 disabled:opacity-40"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Read-only notice */}
      <p className="text-xs text-center text-gray-400 dark:text-gray-500">
        The security audit log is read-only. Entries cannot be modified or deleted.
      </p>
    </div>
  );
};

export default SecurityAuditPage;
